import { Text } from '@mantine/core';
import { useEffect, useRef } from 'react';

const LoyaltyWidgetPreview = ({ loyaltySettings, previewBaseUrl, logoUrl, locale = 'en' }) => {
  const iframeRef = useRef(null);
  const loyaltySettingsRef = useRef(loyaltySettings);

  // Compute the target origin once, resolving against the document to handle relative URLs
  const targetOrigin = previewBaseUrl ? new URL(previewBaseUrl, window.location.href).origin : null;

  // Keep the ref in sync so the ready handler can always read the current settings
  useEffect(() => {
    loyaltySettingsRef.current = loyaltySettings;
  }, [loyaltySettings]);

  // Debounced live update: push the latest settings into the iframe 300ms after the last change
  useEffect(() => {
    if (!previewBaseUrl || !targetOrigin) return;

    const timer = setTimeout(() => {
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'RLV_LOYALTY_PREVIEW_THEME', theme: loyaltySettings },
        targetOrigin
      );
    }, 300);

    return () => clearTimeout(timer);
  }, [loyaltySettings, previewBaseUrl, targetOrigin]);

  // Ready handshake: send the current settings as soon as the widget announces it has mounted
  useEffect(() => {
    if (!previewBaseUrl || !targetOrigin) return;

    const handler = (event) => {
      if (event.source === iframeRef.current?.contentWindow && event.data?.type === 'RLV_LOYALTY_PREVIEW_READY') {
        iframeRef.current.contentWindow.postMessage(
          { type: 'RLV_LOYALTY_PREVIEW_THEME', theme: loyaltySettingsRef.current },
          targetOrigin
        );
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [previewBaseUrl, targetOrigin]);

  if (!previewBaseUrl) {
    return (
      <Text size="sm" c="dimmed">
        Live preview isn&apos;t available in this environment.
      </Text>
    );
  }

  // Build iframe src with proper query string handling
  const iframeSrc = (() => {
    const url = new URL(previewBaseUrl, window.location.href);
    const params = new URLSearchParams(url.search);
    params.set('preview', '1');
    params.set('logo', logoUrl || '');
    params.set('l', locale || 'en');
    url.search = params.toString();
    return url.toString();
  })();

  return (
    <iframe
      ref={iframeRef}
      src={iframeSrc}
      title="Loyalty widget preview"
      style={{ width: '100%', height: 560, border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8 }}
    />
  );
};

export default LoyaltyWidgetPreview;
